import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtNum, fmtMoneyCur, PRODUCTO_ESTATUS, PRODUCTO_COLORS } from "../lib/format";
import { Card, SectionTitle } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Save } from "lucide-react";

const MIX_TIPOS = ["habitación hotel", "loft", "condo 1R", "condo 2R", "villa", "local comercial", "otro"];
const EST_ESTATUS = ["Cumple", "En proceso", "No cumple", "N.A."];

export default function FichaProducto() {
  const { pid, upeId } = useParams();
  const { canEdit } = useAuth();
  const [p, setP] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [estModal, setEstModal] = useState(null); // {estatus, comentario}
  const [contactos, setContactos] = useState([]);

  const load = () => api.get(`/upes/${upeId}/producto`).then((r) => { setP(r.data); setDirty(false); });
  useEffect(() => { load(); api.get("/contactos").then((r) => setContactos(r.data.filter((c) => c.tipo === "Operador-Marca"))); }, [upeId]); // eslint-disable-line

  const set = (k, v) => { setP({ ...p, [k]: v }); setDirty(true); };
  const setPre = (k, v) => { setP({ ...p, presupuesto: { ...p.presupuesto, [k]: Number(v) || 0 } }); setDirty(true); };

  const guardar = async () => {
    try {
      const { data } = await api.patch(`/productos/${p.id}`, {
        nombre_comercial: p.nombre_comercial, tipo: p.tipo, segmento: p.segmento, descripcion: p.descripcion,
        operador_nombre: p.operador_nombre, operador_id: p.operador_id || null,
        unit_mix: p.unit_mix, amenidades: p.amenidades, estandares: p.estandares, presupuesto: p.presupuesto,
      });
      setP({ ...p, ...data }); setDirty(false);
      toast.success("Ficha de producto guardada");
    } catch (e) { toast.error(apiError(e)); }
  };

  const cambiarEstatus = async () => {
    try {
      await api.post(`/productos/${p.id}/estatus`, estModal);
      toast.success("Estatus actualizado"); setEstModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!p) return <div className="p-10 text-slate-400 animate-pulse">Cargando ficha de producto...</div>;
  const mix = p.unit_mix || [];
  const sinOperador = !p.operador_nombre || ["Por definir", "Sin marca", ""].includes(p.operador_nombre);

  const row = (arr, i, k, v, num = false) => { const c = [...arr]; c[i] = { ...c[i], [k]: num ? Number(v) || 0 : v }; return c; };

  return (
    <div className="p-4 sm:p-8 max-w-[1200px]">
      <Link to={`/p/${pid}/desarrollo`} className="text-xs text-[#00A3B5] font-bold uppercase tracking-wider flex items-center gap-1 mb-2"><ArrowLeft size={12} /> Portafolio</Link>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-2">
        <div>
          <p className="text-xs tracking-[0.25em] uppercase text-[#00A3B5] font-bold mb-1">Ficha de producto · {p.upe?.clave}</p>
          <h1 className="font-display text-3xl font-bold text-[#002B49]">{p.nombre_comercial || "Producto sin nombre"}</h1>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <Select value={p.estatus} onValueChange={(v) => setEstModal({ estatus: v, comentario: "" })}>
              <SelectTrigger data-testid="producto-estatus-select" className="h-9 w-64 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>{PRODUCTO_ESTATUS.map((e) => <SelectItem key={e} value={e}>
                <span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: PRODUCTO_COLORS[e] }} />{e}</span></SelectItem>)}</SelectContent>
            </Select>)}
          {!canEdit && <span className="text-sm font-semibold px-3 py-1.5 rounded-md" style={{ background: PRODUCTO_COLORS[p.estatus] }}>{p.estatus}</span>}
          {canEdit && <Button data-testid="producto-guardar-button" onClick={guardar} disabled={!dirty} className="h-9 gap-2 bg-[#00A3B5] hover:bg-[#008C9C] text-white"><Save size={14} /> Guardar</Button>}
        </div>
      </div>
      {sinOperador && <p data-testid="warning-sin-operador" className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-4">⚠ Producto sin operador/marca definida</p>}
      {p.excede_autorizado && <p data-testid="warning-excede" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4">⚠ La mezcla de unidades ({p.unidades_definidas}) EXCEDE las llaves/densidad autorizadas de la UPE ({p.unidades_autorizadas})</p>}

      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="p-5">
          <SectionTitle>1. Concepto</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Nombre comercial</Label>
              <Input data-testid="producto-nombre-input" disabled={!canEdit} value={p.nombre_comercial || ""} onChange={(e) => set("nombre_comercial", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Tipo</Label>
              <Input disabled={!canEdit} value={p.tipo || ""} onChange={(e) => set("tipo", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Operador / marca</Label>
              <Select disabled={!canEdit} value={p.operador_nombre || "Por definir"} onValueChange={(v) => { set("operador_nombre", v); }}>
                <SelectTrigger data-testid="producto-operador-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Por definir">Por definir</SelectItem>
                  <SelectItem value="Sin marca">Sin marca</SelectItem>
                  {contactos.map((c) => <SelectItem key={c.id} value={c.nombre}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label>Segmento</Label>
              <Select disabled={!canEdit} value={p.segmento || "upscale"} onValueChange={(v) => set("segmento", v)}>
                <SelectTrigger><SelectValue placeholder="Segmento" /></SelectTrigger>
                <SelectContent>{["lujo", "upper-upscale", "upscale", "midscale", "residencial"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <div className="space-y-1.5 mt-3"><Label>Descripción del concepto</Label>
            <Textarea disabled={!canEdit} rows={3} value={p.descripcion || ""} onChange={(e) => set("descripcion", e.target.value)} /></div>
        </Card>

        <Card className="p-5">
          <SectionTitle>5. Presupuesto paramétrico</SectionTitle>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Costo por m² (MXN)</Label>
              <Input data-testid="pre-costo-m2" disabled={!canEdit} type="number" value={p.presupuesto?.costo_m2 || ""} onChange={(e) => setPre("costo_m2", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>m² totales (0 = calcular del mix)</Label>
              <Input disabled={!canEdit} type="number" value={p.presupuesto?.m2_totales || ""} onChange={(e) => setPre("m2_totales", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Indirectos %</Label>
              <Input disabled={!canEdit} type="number" value={p.presupuesto?.indirectos_pct || ""} onChange={(e) => setPre("indirectos_pct", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Ocupación estimada % (hotel)</Label>
              <Input disabled={!canEdit} type="number" value={p.presupuesto?.ocupacion_pct || ""} onChange={(e) => setPre("ocupacion_pct", e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4 text-center">
            {[["m² a construir", fmtNum(Math.round(p.m2_totales_calc || 0))], ["Costo total", fmtMoneyCur(p.costo_total)],
              ["Ingreso estimado", fmtMoneyCur(p.ingreso_estimado)], ["Margen bruto", `${p.margen_pct}%`]].map(([l, v]) => (
              <div key={l} className="bg-slate-50 rounded-md p-2.5">
                <p className="font-display font-bold text-[#002B49]">{v}</p>
                <p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p>
              </div>))}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">Guarda la ficha para recalcular. Paramétrico — no sustituye el presupuesto de obra.</p>
        </Card>

        <Card className="p-5 lg:col-span-2">
          <SectionTitle right={canEdit && <Button data-testid="mix-agregar" variant="outline" className="h-8 gap-1 text-xs" onClick={() => set("unit_mix", [...mix, { nombre: "", tipo: "habitación hotel", m2: 0, unidades: 0, precio_obj: 0, adr_obj: 0 }])}><Plus size={12} /> Prototipo</Button>}>
            2. Mezcla de unidades ({p.unidades_definidas} / {p.unidades_autorizadas} autorizadas)</SectionTitle>
          <div className="overflow-x-auto">
            <table className="w-full text-xs min-w-[760px]">
              <thead><tr className="text-left text-slate-500 uppercase tracking-wider">
                <th className="py-2 pr-2">Prototipo</th><th className="py-2 pr-2">Tipo</th><th className="py-2 pr-2">m²/unidad</th>
                <th className="py-2 pr-2">Unidades</th><th className="py-2 pr-2">Precio objetivo (venta)</th><th className="py-2 pr-2">ADR objetivo (hotel)</th><th></th></tr></thead>
              <tbody>
                {mix.length === 0 && <tr><td colSpan={7} className="py-4 text-center text-slate-400">Sin prototipos. Agrega la mezcla de unidades del producto.</td></tr>}
                {mix.map((m, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="py-1.5 pr-2"><Input data-testid={`mix-nombre-${i}`} disabled={!canEdit} className="h-8 text-xs" value={m.nombre} onChange={(e) => set("unit_mix", row(mix, i, "nombre", e.target.value))} /></td>
                    <td className="py-1.5 pr-2">
                      <Select disabled={!canEdit} value={m.tipo} onValueChange={(v) => set("unit_mix", row(mix, i, "tipo", v))}>
                        <SelectTrigger className="h-8 text-xs w-36"><SelectValue /></SelectTrigger>
                        <SelectContent>{MIX_TIPOS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select></td>
                    <td className="py-1.5 pr-2"><Input disabled={!canEdit} className="h-8 text-xs w-20" type="number" value={m.m2} onChange={(e) => set("unit_mix", row(mix, i, "m2", e.target.value, true))} /></td>
                    <td className="py-1.5 pr-2"><Input data-testid={`mix-unidades-${i}`} disabled={!canEdit} className="h-8 text-xs w-20" type="number" value={m.unidades} onChange={(e) => set("unit_mix", row(mix, i, "unidades", e.target.value, true))} /></td>
                    <td className="py-1.5 pr-2"><Input disabled={!canEdit} className="h-8 text-xs w-28" type="number" value={m.precio_obj || ""} onChange={(e) => set("unit_mix", row(mix, i, "precio_obj", e.target.value, true))} /></td>
                    <td className="py-1.5 pr-2"><Input disabled={!canEdit} className="h-8 text-xs w-24" type="number" value={m.adr_obj || ""} onChange={(e) => set("unit_mix", row(mix, i, "adr_obj", e.target.value, true))} /></td>
                    <td>{canEdit && <button onClick={() => set("unit_mix", mix.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle right={canEdit && <Button variant="outline" className="h-8 gap-1 text-xs" onClick={() => set("amenidades", [...(p.amenidades || []), { nombre: "", m2: 0 }])}><Plus size={12} /> Amenidad</Button>}>3. Amenidades</SectionTitle>
          <div className="space-y-2">
            {(p.amenidades || []).length === 0 && <p className="text-sm text-slate-400">Sin amenidades (albercas, restaurantes, spa, gimnasio, club de playa...).</p>}
            {(p.amenidades || []).map((a, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input disabled={!canEdit} className="h-8 text-xs flex-1" placeholder="Amenidad" value={a.nombre} onChange={(e) => set("amenidades", row(p.amenidades, i, "nombre", e.target.value))} />
                <Input disabled={!canEdit} className="h-8 text-xs w-24" type="number" placeholder="m²" value={a.m2 || ""} onChange={(e) => set("amenidades", row(p.amenidades, i, "m2", e.target.value, true))} />
                {canEdit && <button onClick={() => set("amenidades", p.amenidades.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>}
              </div>))}
          </div>
        </Card>

        <Card className="p-5">
          <SectionTitle right={canEdit && <Button data-testid="estandar-agregar" variant="outline" className="h-8 gap-1 text-xs" onClick={() => set("estandares", [...(p.estandares || []), { nombre: "", estatus: "En proceso", fecha_compromiso: "" }])}><Plus size={12} /> Estándar</Button>}>4. Estándares del operador ({p.estandares_cumplidos}/{p.estandares_aplicables})</SectionTitle>
          <div className="space-y-2">
            {(p.estandares || []).length === 0 && <p className="text-sm text-slate-400">Sin estándares registrados (entregables de diseño, sistemas, estándares constructivos).</p>}
            {(p.estandares || []).map((es, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input disabled={!canEdit} className="h-8 text-xs flex-1" placeholder="Requisito del operador" value={es.nombre} onChange={(e) => set("estandares", row(p.estandares, i, "nombre", e.target.value))} />
                <Select disabled={!canEdit} value={es.estatus} onValueChange={(v) => set("estandares", row(p.estandares, i, "estatus", v))}>
                  <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>{EST_ESTATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Input disabled={!canEdit} className="h-8 text-xs w-36" type="date" value={es.fecha_compromiso || ""} onChange={(e) => set("estandares", row(p.estandares, i, "fecha_compromiso", e.target.value))} />
                {canEdit && <button onClick={() => set("estandares", p.estandares.filter((_, j) => j !== i))} className="text-slate-300 hover:text-red-500"><Trash2 size={13} /></button>}
              </div>))}
          </div>
        </Card>
      </div>

      <Dialog open={!!estModal} onOpenChange={(v) => !v && setEstModal(null)}>
        <DialogContent className="max-w-sm" data-testid="producto-estatus-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49]">Cambiar estatus del producto</DialogTitle></DialogHeader>
          {estModal && <>
            <p className="text-sm text-slate-600">{p.estatus} → <b>{estModal.estatus}</b></p>
            <Textarea data-testid="producto-estatus-comentario" rows={2} placeholder="Comentario obligatorio (auditoría)" value={estModal.comentario} onChange={(e) => setEstModal({ ...estModal, comentario: e.target.value })} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEstModal(null)}>Cancelar</Button>
              <Button data-testid="producto-estatus-confirmar" onClick={cambiarEstatus} disabled={!estModal.comentario.trim()} className="bg-[#00A3B5] hover:bg-[#008C9C] text-white">Confirmar</Button>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
